import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, CARE_STAFF_ROLES, guardianIncidentsNetwork, unclaimedEmergency, type EmergencyIncident, type Role, type ViewId } from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { useQuery } from '../useQuery';
import { useReadOnly } from '../ui/ReadOnly';

/** The design's dur(): "4m 12s", "1h 06m", "2d 3h". */
export function emergencyAge(ms: number): string {
  const a = Math.abs(ms);
  const mm = Math.floor(a / 60000);
  const h = Math.floor(mm / 60);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h) return `${h}h ${String(mm % 60).padStart(2, '0')}m`;
  return `${mm}m ${String(Math.floor(a / 1000) % 60).padStart(2, '0')}s`;
}

/** Poll for a new emergency this often. */
export const EMERGENCY_POLL_MS = 15_000;

/**
 * The red emergency line at the top of every page but Guardian (design gdBanner, T227,
 * gdLayer L34240), for the people who can go: Care Staff and the Facility Admin. It names
 * the most severe open emergency in the building that nobody has claimed (the shared
 * order: 911 and self-harm or crisis, then other critical, oldest first within), from the live
 * incidents (GET /emergency/incidents). "I'm going" is the real acknowledgement
 * (POST /emergency/acknowledge), which stops the escalation clock. A read-only view
 * never shows it (design: !ro).
 */
/**
 * V9 (design gdBanner, L38761 and the Oct 27 review): Super Admin and Aithora's Operator tier
 * (Director, Support) see the same line across every building, with Open Guardian only; "I'm
 * going" stays with the people on site. The Partner (owner) never sees it.
 */
export function bannerAudience(role: Role | null, readOnly: boolean): 'claim' | 'watch' | null {
  if (!readOnly && (role === 'ADMIN' || (role !== null && CARE_STAFF_ROLES.has(role)))) return 'claim';
  if (role === 'SUPER_ADMIN' || role === 'OPERATOR') return 'watch';
  return null;
}

export function EmergencyBanner({ role, view }: { role: Role | null; view: ViewId | null }) {
  const { client } = useSession();
  const readOnly = useReadOnly();
  const who0 = bannerAudience(role, readOnly);
  const canGo = who0 === 'claim';
  // On site: the building's own list. Aithora: every building's (GET /emergency/incidents/network).
  const q = useQuery<{ incidents: EmergencyIncident[] }>(
    (signal) => (who0 === 'watch' ? guardianIncidentsNetwork(client, { signal }) : api.emergencyIncidents(client, { signal })),
    [client, who0],
    { enabled: who0 !== null, pollMs: EMERGENCY_POLL_MS },
  );
  const [now, setNow] = useState(() => Date.now());
  const [claimed, setClaimed] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const em = q.data ? unclaimedEmergency(q.data.incidents) : null;

  useEffect(() => {
    if (!em) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [em]);

  if (!who0 || view === 'guardian' || !em || claimed === em.id) return null;
  const age = emergencyAge(now - Date.parse(em.created_at));
  const who = em.residentName ?? 'A resident';
  const claim = async () => {
    setFailed(null);
    try {
      const r = await api.emergencyAcknowledge(client, { body: { incidentId: em.id } });
      if (!r.ok) {
        // Someone else got there first: the list says who.
        q.reload();
        return;
      }
      setClaimed(em.id);
      q.reload();
    } catch (e) {
      setFailed(e instanceof Error ? e.message : 'The claim did not go through.');
    }
  };
  return (
    <div role="alert" className="embanner">
      <span className="embanner__dot" aria-hidden="true" />
      <span className="embanner__text">
        {`Emergency · ${who}${em.room ? `, Rm ${em.room}` : ''} · ${age} · nobody has claimed it`}
        {failed && <span className="embanner__err">{` (${failed})`}</span>}
      </span>
      {canGo && (
        <button type="button" className="embanner__go" onClick={claim}>
          I{'’'}m going
        </button>
      )}
      <Link to="/guardian" className="embanner__open">
        Open Guardian
      </Link>
    </div>
  );
}
